import React from 'react'
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
} from '@mui/material'
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Storage as StorageIcon,
  Memory as MemoryIcon,
  NetworkCheck as NetworkIcon,
  CloudDone as CloudIcon,
} from '@mui/icons-material'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { tr } from 'date-fns/locale'

const SystemHealth = ({ data, isLoading = false, error = null }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
      case 'online':
      case 'active':
        return 'success'
      case 'warning':
      case 'degraded':
        return 'warning'
      case 'error':
      case 'offline':
      case 'inactive':
        return 'error'
      default:
        return 'default'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
      case 'online':
      case 'active':
        return <CheckCircleIcon />
      case 'warning':
      case 'degraded':
        return <WarningIcon />
      case 'error':
      case 'offline':
      case 'inactive':
        return <ErrorIcon />
      default:
        return <ScheduleIcon />
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case 'healthy':
        return 'Sağlıklı'
      case 'online':
        return 'Çevrimiçi'
      case 'active':
        return 'Aktif'
      case 'warning':
        return 'Uyarı'
      case 'degraded':
        return 'Yavaşlama'
      case 'error':
        return 'Hata'
      case 'offline':
        return 'Çevrimdışı'
      case 'inactive':
        return 'Pasif'
      default:
        return 'Bilinmiyor'
    }
  }

  if (isLoading) {
    return (
      <Card sx={{ height: 550 }}>
        <CardContent>
          <Skeleton variant="text" width="60%" height={32} />
          <Skeleton variant="rectangular" width="100%" height={300} sx={{ mt: 2 }} />
          <Box sx={{ mt: 2 }}>
            {[...Array(4)].map((_, index) => (
              <Skeleton key={index} variant="text" width="100%" height={32} sx={{ mt: 1 }} />
            ))}
          </Box>
        </CardContent>
      </Card>
    )
  }

  // Error state
  if (error) {
    return (
      <Card sx={{ height: 550 }}>
        <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          <ErrorIcon color="error" sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h6" color="error" sx={{ mb: 1 }}>
            Sistem Verileri Yuklenemedi
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {error.message || 'Bir hata olustu'}
          </Typography>
        </CardContent>
      </Card>
    )
  }

  // No data state
  if (!data) {
    return (
      <Card sx={{ height: 550 }}>
        <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          <WarningIcon color="warning" sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            Sistem Verileri Yok
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Sistem sagligi verileri henuz yuklenemedi.
          </Typography>
        </CardContent>
      </Card>
    )
  }

  // Destructure with null safety - no defaults that hide missing data
  const overallStatus = data.overallStatus
  const database = data.database || {}
  const api = data.api || {}
  const externalServices = data.externalServices || {}
  const cronJobs = data.cronJobs || {}
  const systemResources = data.systemResources || {}
  const uptime = data.uptime

  return (
    <Card sx={{ height: 550 }}>
      <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Sistem Sağlığı
          </Typography>
          <Chip
            icon={getStatusIcon(overallStatus)}
            label={getStatusText(overallStatus)}
            color={getStatusColor(overallStatus)}
            size="small"
          />
        </Box>

        {/* System Overview */}
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Uptime: {uptime != null ? `${Math.floor(uptime / 24)}g ${uptime % 24}s` : '-'}
          </Typography>

          {/* System Resources */}
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption">CPU Kullanimi</Typography>
              <Typography variant="caption">
                {systemResources.cpu != null ? `${systemResources.cpu}%` : '-'}
              </Typography>
            </Box>
            <LinearProgress
              variant={systemResources.cpu != null ? "determinate" : "indeterminate"}
              value={systemResources.cpu ?? 0}
              color={systemResources.cpu > 80 ? 'error' : systemResources.cpu > 60 ? 'warning' : 'success'}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>

          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption">Bellek Kullanimi</Typography>
              <Typography variant="caption">
                {systemResources.memory != null ? `${systemResources.memory}%` : '-'}
              </Typography>
            </Box>
            <LinearProgress
              variant={systemResources.memory != null ? "determinate" : "indeterminate"}
              value={systemResources.memory ?? 0}
              color={systemResources.memory > 80 ? 'error' : systemResources.memory > 60 ? 'warning' : 'success'}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>

          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption">Disk Kullanimi</Typography>
              <Typography variant="caption">
                {systemResources.disk != null ? `${systemResources.disk}%` : '-'}
              </Typography>
            </Box>
            <LinearProgress
              variant={systemResources.disk != null ? "determinate" : "indeterminate"}
              value={systemResources.disk ?? 0}
              color={systemResources.disk > 80 ? 'error' : systemResources.disk > 60 ? 'warning' : 'success'}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Service Status */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <List dense sx={{ p: 0 }}>
            <ListItem sx={{ px: 0, py: 0.7 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <StorageIcon color={getStatusColor(database.status)} />
              </ListItemIcon>
              <ListItemText
                primary="Veritabani"
                secondary={database.responseTime != null ? `${database.responseTime}ms` : '-'}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                secondaryTypographyProps={{ variant: 'caption' }}
                sx={{ mr: 1 }}
              />
              <Chip
                label={getStatusText(database.status)}
                size="small"
                color={getStatusColor(database.status)}
                variant="outlined"
              />
            </ListItem>

            <ListItem sx={{ px: 0, py: 0.7 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <NetworkIcon color={getStatusColor(api.status)} />
              </ListItemIcon>
              <ListItemText
                primary="API Servisleri"
                secondary={api.responseTime != null ? `${api.responseTime}ms` : '-'}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                secondaryTypographyProps={{ variant: 'caption' }}
                sx={{ mr: 1 }}
              />
              <Chip
                label={getStatusText(api.status)}
                size="small"
                color={getStatusColor(api.status)}
                variant="outlined"
              />
            </ListItem>

            <ListItem sx={{ px: 0, py: 0.7 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CloudIcon color={getStatusColor(externalServices.status)} />
              </ListItemIcon>
              <ListItemText
                primary="Dis Servisler"
                secondary="SMSPool"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                secondaryTypographyProps={{ variant: 'caption' }}
                sx={{ mr: 1 }}
              />
              <Chip
                label={getStatusText(externalServices.status)}
                size="small"
                color={getStatusColor(externalServices.status)}
                variant="outlined"
              />
            </ListItem>

            <ListItem sx={{ px: 0, py: 0.7 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <ScheduleIcon color={getStatusColor(cronJobs.status)} />
              </ListItemIcon>
              <ListItemText
                primary="Zamanlanmis Isler"
                secondary={
                  cronJobs.lastRun
                    ? formatDistanceToNow(parseISO(cronJobs.lastRun), { addSuffix: true, locale: tr })
                    : '-'
                }
                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                secondaryTypographyProps={{ variant: 'caption' }}
                sx={{ mr: 1 }}
              />
              <Chip
                label={getStatusText(cronJobs.status)}
                size="small"
                color={getStatusColor(cronJobs.status)}
                variant="outlined"
              />
            </ListItem>
          </List>
        </Box>
      </CardContent>
    </Card>
  )
}

export default SystemHealth